import { memo, useCallback, useMemo } from "react";
import { Pressable } from "react-native";
import "@visioni/a3s-ui/theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Chip, Icon } from "@visioni/a3s-ui/components";

export type UserPermissionRole = "tenant_admin" | "site_manager" | "site_viewer" | "camera_manager" | "camera_viewer";

export type UserPermissionChip = {
  label: string;
  role: UserPermissionRole;
  siteId?: string;
  cameraId?: string;
};

export type UserListItem = {
  id: string;
  name: string;
  email?: string;
  permissions: UserPermissionChip[];
};

export type UserListTableProps = {
  users: UserListItem[];
  onEditUser: (userId: string) => void;
  onPressUser?: (userId: string) => void;
};

function getVisibleChips(permissions: UserPermissionChip[]): UserPermissionChip[] {
  const hasTenantAdmin = permissions.some((p) => p.role === "tenant_admin");
  if (hasTenantAdmin) return permissions.filter((p) => p.role === "tenant_admin");

  const siteManageCount = permissions.filter((p) => p.role === "site_manager").length;
  const siteViewerCount = permissions.filter((p) => p.role === "site_viewer").length;
  const cameraManageCount = permissions.filter((p) => p.role === "camera_manager").length;
  const cameraViewerCount = permissions.filter((p) => p.role === "camera_viewer").length;

  const addChip = (count: number, label: string, role: UserPermissionRole) => {
    if (count > 0) return [{ label: `${label} (${count.toString()})`, role }];
    return [];
  };

  return [
    ...addChip(siteManageCount, "Manage Site", "site_manager"),
    ...addChip(siteViewerCount, "View Site", "site_viewer"),
    ...addChip(cameraManageCount, "Manage Camera", "camera_manager"),
    ...addChip(cameraViewerCount, "View Camera", "camera_viewer"),
  ];
}

export const UserListTable = memo(({ users, onEditUser, onPressUser }: UserListTableProps) => {
  if (users.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyState}>
          <Text textStyle="sm/regular" color="textMuted">
            No users found
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {users.map((user, index) => (
        <UserListRow
          key={user.id}
          user={user}
          isLast={index === users.length - 1}
          onEdit={onEditUser}
          {...(onPressUser ? { onPress: onPressUser } : {})}
        />
      ))}
    </View>
  );
});

const UserListRow = memo(
  ({
    user,
    isLast,
    onEdit,
    onPress,
  }: {
    user: UserListItem;
    isLast: boolean;
    onEdit: (userId: string) => void;
    onPress?: (userId: string) => void;
  }) => {
    const { theme } = useUnistyles();
    const isLastItemStyle = useMemo(() => ({ borderBottomWidth: isLast ? 0 : 1 }), [isLast]);
    const visibleChips = useMemo(() => getVisibleChips(user.permissions), [user.permissions]);

    const handlePress = useCallback(() => {
      onPress?.(user.id);
    }, [onPress, user.id]);

    const handleEdit = useCallback(
      (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        onEdit(user.id);
      },
      [onEdit, user.id],
    );

    return (
      <Pressable onPress={handlePress} disabled={!onPress}>
        <View bg="surfaceCard" flexDirection="row" alignItems="center" style={[styles.row, isLastItemStyle]}>
          <View style={styles.userInfo}>
            <Text textStyle="sm/medium" numberOfLines={1}>
              {user.name || user.email}
            </Text>
            {user.email && user.name && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {user.email}
              </Text>
            )}
          </View>

          <View style={styles.chipsContainer}>
            {visibleChips.map((chip) => (
              <Chip key={chip.label} label={chip.label} variant="outlined" />
            ))}
          </View>

          <Pressable onPress={handleEdit} hitSlop={8} style={styles.editButton}>
            <Icon name="edit" size={18} color={theme.colors.text} />
          </Pressable>
        </View>
      </Pressable>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
  },
  row: {
    padding: theme.spacing["3xl"],
    borderBottomColor: theme.colors.borderCard,
    gap: theme.spacing.md,
  },
  userInfo: {
    flex: 1,
    gap: 2,
    minWidth: 120,
  },
  chipsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.xs,
    flexShrink: 1,
  },
  editButton: {
    padding: theme.spacing.sm,
    borderRadius: theme.spacing.sm,
    backgroundColor: theme.colors.muted,
  },
  emptyState: {
    padding: theme.spacing["3xl"],
    alignItems: "center",
    justifyContent: "center",
  },
}));
